import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Medialink } from '../../../../api/medialink';
import { Module } from '../../../../api/module';
import { TOKEN_ACCESSOR } from '../../../../utils/tokenAccessor';
import { AnalyticsData } from '../../hooks/constants/Analytics';
import { ExercisePhase } from './@types/exercise-types';
import {
  initAssignmentSession,
  InitExerciseSessionResponse,
  initFreeExerciseSession,
  PauseExerciseSessionResponse,
  StopExerciseSessionResponse,
} from './api/exercises-api';
import ExerciseActivityModals from './components/exercise-activity-modals';
import ExerciseLaunchModals from './components/exercise-launch-modals';
import ExerciseProgressionModals from './components/exercise-progression-modals';
import { ExerciseManagerContextProvider, useExerciseManagerContext } from './contexts/ExerciseManagerContext';
import { ExerciseTranslationsContext, ExerciseTranslationsContextValue } from './contexts/ExerciseTranslationsContext';

import './exercise-dialogs.css';

type ExerciseManagerProps = {
  translations: ExerciseTranslationsContextValue;
  language: SupportedUserLanguage;
  medialink: Medialink;
  initialPhase?: ExercisePhase;
  assignmentId?: string;
  onError: (err: unknown) => void;
  onClose: () => void;
  onSessionPaused?: () => void;
  onSessionFinished?: () => void;
  analyticsData?: AnalyticsData;
  studioGroup: string;
  selectedModule: Module;
};

const ExerciseManagerWrappedWithContext = ({
  medialink,
  translations,
  language,
  onError,
  onClose,
  initialPhase,
  assignmentId,
  onSessionPaused,
  onSessionFinished,
  analyticsData,
  studioGroup,
  selectedModule,
}: ExerciseManagerProps): JSX.Element => {
  const [phaseAction, setPhaseAction] = useState<ExercisePhase | undefined>(initialPhase);

  useEffect(() => {
    if (initialPhase) setPhaseAction(initialPhase);
  }, [initialPhase]);

  return (
    <ExerciseTranslationsContext.Provider value={translations}>
      <ExerciseManagerContextProvider
        medialink={medialink}
        module={selectedModule}
        onClose={onClose}
        studioGroup={studioGroup}
        language={language}
        assignmentId={assignmentId}
      >
        <ExerciseManager
          onError={onError}
          setPhaseAction={setPhaseAction}
          analyticsData={analyticsData}
          phaseAction={phaseAction}
          onSessionPaused={onSessionPaused}
          onSessionFinished={onSessionFinished}
        />
      </ExerciseManagerContextProvider>
    </ExerciseTranslationsContext.Provider>
  );
};

interface Props
  extends Pick<
    ExerciseManagerProps,
    'onError' | 'analyticsData' | 'onSessionPaused' | 'onSessionFinished' | 'initialPhase'
  > {
  phaseAction: ExercisePhase | undefined;
  setPhaseAction: Dispatch<SetStateAction<ExercisePhase | undefined>>;
}

const ExerciseManager = ({
  onError,
  setPhaseAction,
  phaseAction,
  analyticsData,
  onSessionPaused,
  onSessionFinished,
}: Props) => {
  const { phase, setPhase, medialink, module, studioGroup, assignmentId } = useExerciseManagerContext();

  const [exerciseSession, setExerciseSession] = useState<InitExerciseSessionResponse>();
  const [exercisePausedSession, setExercisePausedSession] = useState<PauseExerciseSessionResponse>();
  const [exerciseFinishedSession, setExerciseFinishedSession] = useState<StopExerciseSessionResponse>();

  const [reviewSessionId, setReviewSessionId] = useState<string>();

  useEffect(() => {
    if (reviewSessionId) {
      // phase action is reset to undefined when the reviewSessionId is set, so this will rerender
      return;
    }

    async function getExerciseSessionInfo(medialink: Medialink) {
      const token = await TOKEN_ACCESSOR.tokenGetter();

      return assignmentId
        ? initAssignmentSession(token!, module.id, assignmentId, studioGroup)
        : initFreeExerciseSession(token!, module.id, medialink.id, studioGroup);
    }

    if (!exerciseSession) {
      getExerciseSessionInfo(medialink)
        .then(exerciseSession => {
          setExerciseSession(exerciseSession);

          if (exerciseSession.result) {
            setPhase('launch');
          } else if (exerciseSession) {
            setPhase('activity');
            setPhaseAction('start');
          }
        })
        .catch(onError);
    }
  }, [
    assignmentId,
    module.id,
    module,
    setPhase,
    onError,
    studioGroup,
    medialink,
    setPhaseAction,
    phaseAction,
    reviewSessionId,
    exerciseSession,
  ]);

  const handleSessionPaused = (session: PauseExerciseSessionResponse) => {
    setExercisePausedSession(session);

    onSessionPaused?.();
  };

  const handleSessionFinished = (session: StopExerciseSessionResponse) => {
    setExerciseFinishedSession(session);

    onSessionFinished?.();
  };

  const handleResume = () => {
    setPhaseAction('resume');
    setPhase('activity');
    setExercisePausedSession(undefined);
  };

  const handleRestart = () => {
    setPhaseAction('start');
    setPhase('activity');
    setExercisePausedSession(undefined);
    setExerciseFinishedSession(undefined);
  };

  const handleReview = (sessionId: string) => {
    setReviewSessionId(sessionId);
    setPhaseAction(undefined);
    setPhase('activity');
  };

  return (
    <>
      {phase === 'launch' && (
        <ExerciseLaunchModals
          exerciseSession={exerciseSession}
          onReview={handleReview}
          onRestart={handleRestart}
          onResume={handleResume}
        />
      )}
      {phase === 'activity' && (
        <ExerciseActivityModals
          reviewSessionId={reviewSessionId}
          phaseAction={phaseAction}
          analyticsData={analyticsData}
          onSessionPaused={handleSessionPaused}
          onSessionFinished={handleSessionFinished}
          setPhase={setPhase}
          session={exerciseFinishedSession?.result}
        />
      )}
      {phase === 'progression' && (
        <ExerciseProgressionModals
          pausedSession={exercisePausedSession}
          finishedSession={exerciseFinishedSession}
          onResume={handleResume}
          onRestart={handleRestart}
          onReview={handleReview}
        />
      )}
    </>
  );
};

export { ExerciseManagerWrappedWithContext as ExerciseManager };
